import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useSport } from '../context/SportContext';
import HeaderStats from '../components/HeaderStats';
import FilterSection from '../components/FilterSection';
import NHLConfidenceTiers from '../components/NHLConfidenceTiers';
import { enrichPredictionsWithResults } from '../utils/api';
const NHLDashboard = () => {
    console.log('🏒 NHLDashboard component is rendering');
    const { currentSport } = useSport();
    const [predictions, setPredictions] = useState(null);
    const [filteredGames, setFilteredGames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        team: '',
        confidence: 'ALL',
        conference: 'ALL',
        week: 'ALL',
        date: 'ALL'
    });
    useEffect(() => {
        const loadPredictions = async () => {
            try {
                setLoading(true);
                const timestamp = Date.now();
                const response = await fetch(`/static/nhl/nhl_predictions.json?v=${timestamp}`, {
                    headers: {
                        'Cache-Control': 'no-cache, no-store, must-revalidate',
                        'Pragma': 'no-cache',
                        'Expires': '0'
                    }
                });
                console.log('🏒 Fetching NHL predictions with timestamp:', timestamp);
                if (!response.ok) {
                    throw new Error('Failed to fetch NHL predictions');
                }
                const data = await response.json();
                console.log('🏒 Loaded predictions data:', data);
                console.log('🏒 Data has predictions:', !!data?.predictions);
                console.log('🏒 Predictions count:', data?.predictions?.length);
                // Transform the data structure to match what the components expect
                const rawGames = data.predictions || [];
                console.log('🏒 First raw game:', rawGames[0]);
                const games = rawGames.map((game) => {
                    // Calculate confidence level based on probability and betting edge
                    const winProb = game.home_win_probability || 0.5;
                    const edge = game.betting_edge || 0;
                    let confidenceLevel = 'LOW';
                    if (winProb >= 0.70 && edge >= 0.15)
                        confidenceLevel = 'HIGH';
                    else if (winProb >= 0.65 && edge >= 0.12)
                        confidenceLevel = 'MEDIUM_PLUS';
                    else if (winProb >= 0.60 && edge >= 0.08)
                        confidenceLevel = 'MEDIUM';
                    // Calculate display confidence (0-100 scale)
                    const displayConfidence = Math.max(winProb, 1 - winProb) * 100;
                    return {
                        id: game.game_id || `game_${Math.random()}`,
                        game_id: game.game_id || `game_${Math.random()}`,
                        home_team: game.home_team,
                        away_team: game.away_team,
                        game_date: game.date,
                        game_time: '7:00 PM', // NHL typically has evening games
                        venue: `${game.home_team} Arena`,
                        week: 1, // NHL doesn't use weeks, use game number or date
                        year: 2025,
                        // Prediction fields
                        confidence_level: confidenceLevel,
                        confidence: game.confidence || displayConfidence / 100,
                        team_to_cover: game.predicted_winner === 'HOME' ? game.home_team : game.away_team,
                        predicted_winner: game.predicted_winner,
                        home_win_probability: game.home_win_probability,
                        away_win_probability: game.away_win_probability,
                        display_confidence: displayConfidence,
                        ensemble_probability: Math.max(game.home_win_probability, game.away_win_probability),
                        // Betting fields
                        home_ml: game.home_ml,
                        away_ml: game.away_ml,
                        spread: game.spread,
                        total: game.total,
                        betting_edge: game.betting_edge,
                        recommendation: game.recommendation,
                        // Individual model predictions
                        individual_predictions: game.individual_predictions,
                        individual_probabilities: game.individual_probabilities,
                        features_used: game.features_used,
                        // Store original data for reference
                        original_prediction_data: game
                    };
                });
                const transformedData = {
                    metadata: data.metadata || data.summary || data,
                    games: games
                };
                console.log('🏒 Transformed games sample:', games[0]);
                console.log('🏒 Games with IDs:', games.map((g) => ({ id: g.id, teams: `${g.away_team} @ ${g.home_team}` })));
                // Enrich with game results
                console.log('🏒 Enriching with game results...');
                const enrichedData = await enrichPredictionsWithResults(transformedData, 'nhl');
                console.log('🏒 Enriched data:', enrichedData);
                setPredictions(enrichedData);
                setFilteredGames(enrichedData.games);
            }
            catch (err) {
                setError('Failed to load NHL predictions. Please try again later.');
                console.error('Error loading NHL predictions:', err);
            }
            finally {
                setLoading(false);
            }
        };
        loadPredictions();
    }, [currentSport]);
    useEffect(() => {
        if (!predictions)
            return;
        let filtered = predictions.games;
        // Apply team filter
        if (filters.team) {
            const teamLower = filters.team.toLowerCase();
            filtered = filtered.filter((game) => game.home_team.toLowerCase().includes(teamLower) ||
                game.away_team.toLowerCase().includes(teamLower));
        }
        // Apply confidence filter
        if (filters.confidence !== 'ALL') {
            filtered = filtered.filter((game) => game.confidence_level === filters.confidence);
        }
        console.log('🏒 NHL filtered games:', filtered.length);
        if (filtered.length > 0) {
            console.log('🏒 First game structure:', filtered[0]);
            console.log('🏒 Confidence levels in data:', [...new Set(filtered.map((g) => g.confidence_level))]);
        }
        setFilteredGames(filtered);
    }, [predictions, filters]);
    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
    };
    if (loading) {
        return (_jsx("div", { className: "container-mobile safe-area-padding", children: _jsx("div", { className: "d-flex justify-content-center", children: _jsx("div", { className: "spinner-border", role: "status", children: _jsx("span", { className: "visually-hidden", children: "Loading..." }) }) }) }));
    }
    if (error) {
        return (_jsx("div", { className: "container-mobile safe-area-padding", children: _jsxs("div", { className: "alert alert-danger", role: "alert", children: [_jsx("i", { className: "fas fa-exclamation-triangle me-2" }), error] }) }));
    }
    if (!predictions) {
        return (_jsx("div", { className: "container-mobile safe-area-padding", children: _jsx("div", { className: "alert alert-info", role: "alert", children: "No NHL predictions available at this time." }) }));
    }
    return (_jsxs("div", { className: "container-mobile safe-area-padding", children: [_jsx(HeaderStats, { metadata: predictions?.metadata, games: predictions?.games || [], sport: currentSport }), _jsx(FilterSection, { filters: filters, onFilterChange: handleFilterChange, availableWeeks: [] }), _jsx(NHLConfidenceTiers, { games: filteredGames })] }));
};
export default NHLDashboard;
